import React from "react";
import { format } from "date-fns";
import PropTypes from "prop-types";
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  Typography,
  TableCell,
  TableSortLabel,
  Container,
  Button,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { COLORS } from "../../constants/styles";
import ListPagination from "../ListPagination";

const useStyles = makeStyles({
  container: {
    paddingLeft: "0px",
    paddingRight: "0px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  title: {
    fontSize: "10px",
    fontWeight: "bold",
  },
  cell: {
    padding: "10px 5px",
    textAlign: "center",
  },
  body: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 180, // tweak
  },
  bodyWrap: {
    whiteSpace: "normal",
  },
  button: {
    padding: "0px",
  },
  buttonTitle: {
    color: COLORS.TEXT_GREEN,
    fontSize: "9px",
    fontWeight: "bold",
  },
  rowGrey: {
    backgroundColor: COLORS.BACKGROUND_GREY,
  },
  rowWhite: {
    backgroundColor: COLORS.BACKGROUND_WHITE,
  },
  thumb: {
    maxHeight: 32,
    maxWidth: 64,
    objectFit: "contain",
    borderRadius: 2,
  },
});

const safeFormatDate = (value) => {
  if (!value) return "";
  try {
    return format(new Date(value), "yyyy-MM-dd");
  } catch (e) {
    return "";
  }
};

const DataTable = ({
  updateItem,
  fetchData,
  pagination,
  removeRow,
  onChangePage,
  updateData,
  fieldsRow,
  actions,
}) => {
  const classes = useStyles();

  const defaultActions = [
    { key: "delete", title: "Delete", onClick: (row) => removeRow(row.id) },
    { key: "edit", title: "Edit", onClick: (row) => updateItem(row) },
  ];

  const resolvedActions =
    Array.isArray(actions) && actions.length > 0 ? actions : defaultActions;

  const safeFetchData = Array.isArray(fetchData) ? fetchData : [];
  const safeFieldsRow =
    fieldsRow && typeof fieldsRow === "object" ? fieldsRow : {};

  const safeRows = (() => {
    try {
      const mapped = updateData ? updateData(safeFetchData) : safeFetchData;
      return Array.isArray(mapped) ? mapped : [];
    } catch (e) {
      return [];
    }
  })();

  const currentSort = pagination?.sort;
  const currentIsAscending = Boolean(pagination?.isAscending);

  const toBackendSortField = (fieldKey) => {
    if (!fieldKey) return currentSort;
    if (fieldKey === "id") return currentSort === "ID" ? "ID" : "Id";
    return `${fieldKey.charAt(0).toUpperCase()}${fieldKey.slice(1)}`;
  };

  const handleRequestSort = (fieldKey) => {
    const nextSort = toBackendSortField(fieldKey);
    const isSameColumn = nextSort === currentSort;
    const nextIsAscending = isSameColumn ? !currentIsAscending : true;

    if (typeof onChangePage !== "function") return;

    onChangePage({
      ...pagination,
      page: 0,
      sort: nextSort,
      isAscending: nextIsAscending,
    });
  };
  console.log("DataTable safeRows[0] =", safeRows?.[0]);

  return (
    <Container data-container="container" className={classes.container}>
      <TableContainer component={Paper} style={{ overflowX: "auto" }}>
        <Table style={{ minWidth: 1200 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              {Object.keys(safeFieldsRow).map((fieldKey) => {
                const title = safeFieldsRow[fieldKey];
                const sortField = toBackendSortField(fieldKey);
                const isActive = sortField === currentSort;

                return (
                  <TableCell
                    key={fieldKey}
                    className={classes.cell}
                    sortDirection={
                      isActive ? (currentIsAscending ? "asc" : "desc") : false
                    }
                  >
                    <TableSortLabel
                      active={isActive}
                      direction={
                        isActive ? (currentIsAscending ? "asc" : "desc") : "asc"
                      }
                      onClick={() => handleRequestSort(fieldKey)}
                    >
                      <Typography className={classes.title}>{title}</Typography>
                    </TableSortLabel>
                  </TableCell>
                );
              })}

              {resolvedActions.map((action) => (
                <TableCell key={action.key} className={classes.cell}>
                  <Typography className={classes.title}>
                    {action.title}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {safeRows.map((row, index) => {
              const styleRow = index % 2 ? classes.rowWhite : classes.rowGrey;

              const initialRowValue =
                safeFetchData.find(({ id }) => id === row?.id) || {};
              const fullRow =
                initialRowValue && Object.keys(initialRowValue).length > 0
                  ? initialRowValue
                  : row;

              return (
                <TableRow key={row?.id ?? index} className={styleRow}>
                  {Object.keys(safeFieldsRow).map((key) => {
                    // compute display value safely, but DO NOT return JSX from helper
                    let textBody = "";

                    const value = row?.[key] ?? fullRow?.[key];

                    if (key === "creationDate") {
                      textBody = safeFormatDate(value);
                    } else if (key === "conference") {
                      // Prefer display-ready string from the mapped row
                      if (typeof value === "string") {
                        textBody = value;
                      } else {
                        // Otherwise fall back to the original DTO's conference name if present
                        textBody = fullRow?.conference?.name ?? "";
                      }
                    } else if (key === "governingBodies") {
                      const list = value;
                      textBody = Array.isArray(list)
                        ? list
                            .map((gb) => gb?.abbreviation)
                            .filter(Boolean)
                            .join(", ")
                        : "";
                    } else if (key === "imageUrl") {
                      textBody = "";
                    } else {
                      textBody = value ?? "";
                    }

                    return (
                      <TableCell key={key} className={classes.cell}>
                        {key === "imageUrl" ? (
                          (() => {
                            const url = (
                              fullRow?.imageSmallUrl ||
                              fullRow?.imageUrl ||
                              ""
                            ).trim();
                            if (!url) return null;
                            return (
                              <img
                                src={url}
                                alt="ad"
                                className={classes.thumb}
                                onError={(e) => {
                                  try {
                                    e.currentTarget.style.display = "none";
                                  } catch (_) {}
                                }}
                              />
                            );
                          })()
                        ) : (
                          <Typography className={classes.body}>
                            {textBody}
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}

                  {resolvedActions.map((action) => {
                    const isVisible = action.isVisible
                      ? action.isVisible(fullRow)
                      : true;

                    if (!isVisible) {
                      return (
                        <TableCell key={action.key} className={classes.cell}>
                          {/* intentionally blank */}
                        </TableCell>
                      );
                    }

                    const isDisabled = action.isDisabled
                      ? action.isDisabled(fullRow)
                      : false;

                    return (
                      <TableCell key={action.key} className={classes.cell}>
                        <Button
                          variant="text"
                          className={classes.button}
                          disabled={isDisabled}
                          onClick={() => action.onClick(fullRow)}
                        >
                          <Typography className={classes.buttonTitle}>
                            {action.getLabel
                              ? action.getLabel(fullRow)
                              : action.title}
                          </Typography>
                        </Button>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <ListPagination filters={pagination} fetchFunction={onChangePage} />
    </Container>
  );
};

DataTable.propTypes = {
  updateItem: PropTypes.func,
  fetchData: PropTypes.array,
  pagination: PropTypes.shape({
    limit: PropTypes.number,
    page: PropTypes.number,
    total: PropTypes.number,
    sort: PropTypes.string,
    isAscending: PropTypes.bool,
  }),
  removeRow: PropTypes.func,
  onChangePage: PropTypes.func,
  updateData: PropTypes.func,
  fieldsRow: PropTypes.object,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      getLabel: PropTypes.func,
      isDisabled: PropTypes.func,
      isVisible: PropTypes.func,
    }),
  ),
};

export default DataTable;
